import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { Plus, Calendar, Clock, CheckCircle, XCircle, User, Smartphone, Trash2 } from 'lucide-react';
import Modal from './Modal';

interface PhoneReplacement {
  id: number;
  user_id: number;
  old_phone_id?: number;
  new_phone_id: number;
  status: 'pending' | 'scheduled' | 'completed' | 'cancelled';
  replacement_reason: string;
  scheduled_date?: string;
  completed_date?: string;
  notes?: string;
  user_name: string;
  new_brand: string;
  new_model: string;
  new_inventory: string;
  old_brand?: string;
  old_model?: string;
  old_inventory?: string;
  appointment_date?: string;
  appointment_status?: string;
  created_at: string;
}

interface MobilePhone {
  id: number;
  brand: string;
  model: string;
  inventory_number: string;
  status: string;
  assigned_user_id?: number;
  is_old_model?: boolean;
}

interface User {
  id: number;
  name: string;
  email: string;
}

const PhoneReplacementPage: React.FC = () => {
  const { user } = useAuth();
  const [replacements, setReplacements] = useState<PhoneReplacement[]>([]);
  const [availablePhones, setAvailablePhones] = useState<MobilePhone[]>([]);
  const [assignedPhones, setAssignedPhones] = useState<MobilePhone[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [phoneSearchTerm, setPhoneSearchTerm] = useState('');
  const [oldPhoneSearchTerm, setOldPhoneSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [userFilter, setUserFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    user_id: 0,
    old_phone_id: 0,
    new_phone_id: 0,
    replacement_reason: '',
    notes: '',
    scheduled_date: '',
    scheduled_time: '',
    assigned_admin_id: 0,
    location_id: 0,
    ticket_number: ''
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const [adminAvailability, setAdminAvailability] = useState<any>(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [showAppointmentHistory, setShowAppointmentHistory] = useState(false);
  const [userAppointments, setUserAppointments] = useState<any[]>([]);

  // Kullanıcı yetki kontrolü
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [replacementsRes, phonesRes, allPhonesRes, usersRes] = await Promise.all([
        fetch('/api/phone-replacements', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        fetch('/api/mobile-phones?recent=true', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        fetch('/api/mobile-phones', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        fetch('/api/users', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        })
      ]);

      if (replacementsRes.ok) setReplacements(await replacementsRes.json());
      if (phonesRes.ok) {
        const availablePhonesData = await phonesRes.json();
        console.log('Available phones received:', availablePhonesData.length, availablePhonesData);
        setAvailablePhones(availablePhonesData);
      } else {
        console.error('Failed to fetch available phones:', phonesRes.status, await phonesRes.text());
      }
      if (allPhonesRes.ok) {
        const allPhonesData = await allPhonesRes.json();
        // Sadece kullanıcıya atanmış telefonları filtrele (assigned_user_id null değil)
        const assignedData = allPhonesData.filter((phone: any) => phone.assigned_user_id !== null && phone.assigned_user_id !== undefined);
        setAssignedPhones(assignedData);
      }
      if (usersRes.ok) setUsers(await usersRes.json());
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error('Hata', 'Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadAdminAvailability = async (adminId: number) => {
    if (!adminId) return;

    try {
      setLoadingCalendar(true);
      const response = await fetch(`/api/phone-appointments/admin-availability-calendar/${adminId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAdminAvailability(data);
        setShowCalendar(true);
      } else {
        showGlobalNotification.error('Hata', 'Takvim verileri yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading calendar:', error);
      showGlobalNotification.error('Hata', 'Takvim yüklenirken hata oluştu');
    } finally {
      setLoadingCalendar(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.user_id || !formData.new_phone_id || !formData.replacement_reason) {
      showGlobalNotification.error('Hata', 'Zorunlu alanları doldurun');
      return;
    }

    try {
      const response = await fetch('/api/phone-replacements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showGlobalNotification.success('Başarılı', 'Telefon değişim kaydı oluşturuldu');
        setShowModal(false);
        setFormData({
          user_id: 0,
          old_phone_id: 0,
          new_phone_id: 0,
          replacement_reason: '',
          notes: '',
          scheduled_date: '',
          scheduled_time: '',
          assigned_admin_id: 0,
          location_id: 0,
          ticket_number: ''
        });
        setUserSearchTerm('');
        setPhoneSearchTerm('');
        setOldPhoneSearchTerm('');
        setShowCalendar(false);
        loadData();
        
        // Notification'ları yenile
        window.dispatchEvent(new CustomEvent('notificationUpdate'));
      } else {
        const error = await response.json();
        showGlobalNotification.error('Hata', error.error || 'Kayıt oluşturulamadı');
      }
    } catch (error) {
      showGlobalNotification.error('Hata', 'Kayıt oluşturulamadı');
    }
  };

  const handleDelete = async (id: number, user_name: string, phone_info: string) => {
    if (!window.confirm(`${user_name} için ${phone_info} telefon değişim kaydını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/phone-replacements/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        showGlobalNotification.success('Başarılı', result.message || 'Telefon değişim kaydı silindi');
        loadData();
        
        // Notification'ları yenile
        window.dispatchEvent(new CustomEvent('notificationUpdate'));
      } else {
        const error = await response.json();
        showGlobalNotification.error('Hata', error.error || 'Kayıt silinemedi');
      }
    } catch (error) {
      showGlobalNotification.error('Hata', 'Kayıt silinemedi');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-primary/20 text-primary-dark';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Bekliyor';
      case 'scheduled': return 'Randevulu';
      case 'completed': return 'Tamamlandı';
      case 'cancelled': return 'İptal Edildi';
      default: return status;
    }
  };

  // Filtrelenmiş kayıtlar
  const getFilteredReplacements = () => {
    let filtered = [...replacements];
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    
    if (userFilter.trim()) {
      filtered = filtered.filter(r =>
        String(r.user_name || '').toLowerCase().includes(userFilter.toLowerCase()) ||
        String(r.new_brand || '').toLowerCase().includes(userFilter.toLowerCase()) ||
        String(r.new_model || '').toLowerCase().includes(userFilter.toLowerCase()) ||
        String(r.new_inventory || '').toLowerCase().includes(userFilter.toLowerCase())
      );
    }
    
    return filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  };

  // Pagination
  const getPaginatedReplacements = () => {
    const filtered = getFilteredReplacements();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(getFilteredReplacements().length / itemsPerPage);
  };

  const getStatusCounts = () => {
    return {
      all: replacements.length,
      pending: replacements.filter(r => r.status === 'pending').length,
      scheduled: replacements.filter(r => r.status === 'scheduled').length,
      completed: replacements.filter(r => r.status === 'completed').length,
      cancelled: replacements.filter(r => r.status === 'cancelled').length
    };
  };

  // Excel Export
  const exportToExcel = () => {
    const filtered = getFilteredReplacements();
    const csvData = [
      ['Kullanıcı', 'Eski Telefon', 'Yeni Telefon', 'Durum', 'Değişim Sebebi', 'Randevu Tarihi', 'Oluşturma Tarihi'],
      ...filtered.map(r => [
        r.user_name,
        r.old_brand ? `${r.old_brand} ${r.old_model} (${r.old_inventory})` : 'Yeni atama',
        `${r.new_brand} ${r.new_model} (${r.new_inventory})`,
        getStatusLabel(r.status),
        r.replacement_reason,
        r.appointment_date ? new Date(r.appointment_date).toLocaleDateString('tr-TR') : '-',
        new Date(r.created_at).toLocaleDateString('tr-TR')
      ])
    ];
    
    const csvContent = csvData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `telefon-degisim-kayitlari-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showGlobalNotification.success('Başarılı', 'Veriler Excel dosyası olarak indirildi');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">Telefon Değişim Sistemi</h1>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">Kullanıcıların telefon değişim işlemlerini yönetin</p>
        </div>
        <div className="flex flex-wrap gap-2 md:gap-3">
          {!isAdmin && (
            <button
              onClick={() => {
                setShowAppointmentHistory(true);
                // Load user appointments
                fetch('/api/phone-appointments/my-appointments', {
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                })
                  .then(res => res.json())
                  .then(data => {
                    console.log('📅 Appointments loaded:', data);
                    setUserAppointments(Array.isArray(data) ? data : []);
                  })
                  .catch(err => {
                    console.error('Error loading appointments:', err);
                    showGlobalNotification.error('Hata', 'Randevu geçmişi yüklenemedi');
                  });
              }}
              className="flex items-center px-3 md:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm md:text-base"
            >
              <Calendar className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Randevu Geçmişim</span>
              <span className="sm:hidden">Geçmiş</span>
            </button>
          )}
          {isAdmin && (
            <button
              onClick={exportToExcel}
              className="flex items-center px-3 md:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm md:text-base"
            >
              <svg className="w-4 h-4 mr-1 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">Excel İndir</span>
              <span className="sm:hidden">Excel</span>
            </button>
          )}
          {!isAdmin && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center px-3 md:px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm md:text-base"
            >
              <Plus className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Yeni Randevu Talebi</span>
              <span className="sm:hidden">Yeni Randevu</span>
            </button>
          )}
        </div>
      </div>

      {/* Filtreleme */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Durum Filtresi</label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'Tümü', count: getStatusCounts().all },
                { key: 'pending', label: 'Bekleyen', count: getStatusCounts().pending },
                { key: 'scheduled', label: 'Randevulu', count: getStatusCounts().scheduled },
                { key: 'completed', label: 'Tamamlandı', count: getStatusCounts().completed },
                { key: 'cancelled', label: 'İptal Edilen', count: getStatusCounts().cancelled }
              ].map(filter => (
                <button
                  key={filter.key}
                  onClick={() => {
                    setStatusFilter(filter.key);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === filter.key
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600'
                  }`}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Arama</label>
            <input
              type="text"
              placeholder="Kullanıcı, telefon marka/model/envanter ara..."
              value={userFilter}
              onChange={(e) => {
                setUserFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6">
        {/* Bekleyen */}
        <div
          onClick={() => {
            setStatusFilter('pending');
            setCurrentPage(1);
          }}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 cursor-pointer transition-all hover:shadow-lg ${
            statusFilter === 'pending' ? 'ring-2 ring-yellow-500 bg-yellow-50' : ''
          }`}
        >
          <div className="flex items-center">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 md:w-6 md:h-6 text-yellow-600" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">Bekleyen</p>
              <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {replacements.filter(r => r.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>

        {/* Randevulu */}
        <div
          onClick={() => {
            setStatusFilter('scheduled');
            setCurrentPage(1);
          }}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 cursor-pointer transition-all hover:shadow-lg ${
            statusFilter === 'scheduled' ? 'ring-2 ring-primary bg-primary/10' : ''
          }`}
        >
          <div className="flex items-center">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 md:w-6 md:h-6 text-primary" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">Randevulu</p>
              <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {replacements.filter(r => r.status === 'scheduled').length}
              </p>
            </div>
          </div>
        </div>

        {/* Tamamlanan */}
        <div
          onClick={() => {
            setStatusFilter('completed');
            setCurrentPage(1);
          }}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 cursor-pointer transition-all hover:shadow-lg ${
            statusFilter === 'completed' ? 'ring-2 ring-green-500 bg-green-50' : ''
          }`}
        >
          <div className="flex items-center">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">Tamamlanan</p>
              <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {replacements.filter(r => r.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>

        {/* Tümü - Mevcut Telefon kartı yerine */}
        <div
          onClick={() => {
            setStatusFilter('all');
            setCurrentPage(1);
          }}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 cursor-pointer transition-all hover:shadow-lg ${
            statusFilter === 'all' ? 'ring-2 ring-purple-500 bg-purple-50' : ''
          }`}
        >
          <div className="flex items-center">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Smartphone className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">Tümü</p>
              <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">{replacements.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Replacements Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100">
              Telefon Değişim Kayıtları ({getFilteredReplacements().length})
            </h3>
            <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              Sayfa {currentPage} / {getTotalPages() || 1}
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Kullanıcı</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Eski Telefon</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Yeni Telefon</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Durum</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Randevu</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tarih</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {getPaginatedReplacements().map((replacement) => (
                <tr key={replacement.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{replacement.user_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {replacement.old_brand ? (
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {replacement.old_brand} {replacement.old_model}
                        <div className="text-xs text-gray-500 dark:text-gray-400">{replacement.old_inventory}</div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">Yeni atama</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {replacement.new_brand} {replacement.new_model}
                      <div className="text-xs text-gray-500 dark:text-gray-400">{replacement.new_inventory}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(replacement.status)}`}>
                      {getStatusLabel(replacement.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {replacement.appointment_date ? (
                      <div>
                        {new Date(replacement.appointment_date).toLocaleDateString('tr-TR')}
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(replacement.appointment_date).toLocaleTimeString('tr-TR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(replacement.created_at).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {isAdmin ? (
                      <button
                        onClick={() => {
                          if (replacement.status !== 'completed') {
                            handleDelete(
                              replacement.id,
                              replacement.user_name,
                              `${replacement.new_brand} ${replacement.new_model} (${replacement.new_inventory})`
                            );
                          }
                        }}
                        disabled={replacement.status === 'completed'}
                        className={`flex items-center px-2 py-1 rounded transition-colors ${
                          replacement.status === 'completed'
                            ? 'text-gray-400 cursor-not-allowed opacity-50'
                            : 'text-red-600 hover:text-red-800 hover:bg-red-50 cursor-pointer'
                        }`}
                        title={replacement.status === 'completed' ? 'Tamamlanmış kayıtlar silinemez' : 'Kaydı Sil'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {getTotalPages() > 1 && (
          <div className="px-4 md:px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs md:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
                Toplam {getFilteredReplacements().length} kayıt, sayfa {currentPage} / {getTotalPages()}
              </div>
              <div className="flex justify-center space-x-1 md:space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-2 md:px-3 py-1 text-xs md:text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Önceki
                </button>

                {Array.from({ length: getTotalPages() }, (_, i) => i + 1)
                  .filter(page =>
                    page === 1 ||
                    page === getTotalPages() ||
                    Math.abs(page - currentPage) <= 1
                  )
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-1 md:px-2 text-gray-400 text-xs md:text-sm">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-2 md:px-3 py-1 text-xs md:text-sm rounded ${
                          currentPage === page
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))
                }

                <button
                  onClick={() => setCurrentPage(Math.min(getTotalPages(), currentPage + 1))}
                  disabled={currentPage === getTotalPages()}
                  className="px-2 md:px-3 py-1 text-xs md:text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sonraki
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal - Sadece non-admin kullanıcılar için */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Yeni Telefon Değişim Kaydı</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kullanıcı *
                  </label>
                  <input
                    type="text"
                    placeholder="Kullanıcı ara..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary mb-2"
                  />
                  <select
                    value={formData.user_id.toString()}
                    onChange={(e) => {
                      const userId = parseInt(e.target.value) || 0;
                      setFormData({...formData, user_id: userId});
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                    size={5}
                  >
                    <option value="0">Kullanıcı seçin</option>
                    {users
                      .filter(u =>
                        userSearchTerm === '' ||
                        String(u.name || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                        String(u.email || '').toLowerCase().includes(userSearchTerm.toLowerCase())
                      )
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Eski Telefon (Geri Alınacak)
                  </label>
                  <input
                    type="text"
                    placeholder="Telefon ara..."
                    value={oldPhoneSearchTerm}
                    onChange={(e) => setOldPhoneSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary mb-2"
                  />
                  <select
                    value={formData.old_phone_id.toString()}
                    onChange={(e) => setFormData({...formData, old_phone_id: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    size={5}
                  >
                    <option value="0">Eski telefon yok (Yeni atama)</option>
                    {(() => {
                      const filteredByUser = assignedPhones.filter(phone =>
                        formData.user_id === 0 || phone.assigned_user_id === formData.user_id
                      );


                      const finalFiltered = filteredByUser.filter(phone =>
                        oldPhoneSearchTerm === '' ||
                        String(phone.brand || '').toLowerCase().includes(oldPhoneSearchTerm.toLowerCase()) ||
                        String(phone.model || '').toLowerCase().includes(oldPhoneSearchTerm.toLowerCase()) ||
                        String(phone.inventory_number || '').toLowerCase().includes(oldPhoneSearchTerm.toLowerCase())
                      );

                      return finalFiltered.map(phone => (
                        <option key={phone.id} value={phone.id}>
                          {phone.brand} {phone.model} ({phone.inventory_number})
                        </option>
                      ));
                    })()}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Kullanıcıdan geri alınacak telefon. Yeni atama için boş bırakın.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Yeni Telefon (Atanacak) *
                  </label>
                  <input
                    type="text"
                    placeholder="Telefon ara..."
                    value={phoneSearchTerm}
                    onChange={(e) => setPhoneSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary mb-2"
                  />
                  <select
                    value={formData.new_phone_id.toString()}
                    onChange={(e) => setFormData({...formData, new_phone_id: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                    size={5}
                  >
                    <option value="0">Telefon seçin</option>
                    {availablePhones
                      .filter(phone =>
                        phoneSearchTerm === '' ||
                        String(phone.brand || '').toLowerCase().includes(phoneSearchTerm.toLowerCase()) ||
                        String(phone.model || '').toLowerCase().includes(phoneSearchTerm.toLowerCase()) ||
                        String(phone.inventory_number || '').toLowerCase().includes(phoneSearchTerm.toLowerCase())
                      )
                      .map(phone => (
                        <option key={phone.id} value={phone.id}>
                          {phone.brand} {phone.model} ({phone.inventory_number})
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Son 30 gün içinde oluşturulan telefonlar gösteriliyor
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Değişim Sebebi *
                  </label>
                  <textarea
                    value={formData.replacement_reason}
                    onChange={(e) => setFormData({...formData, replacement_reason: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notlar
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={2}
                  />
                </div>

                {/* Randevu Oluştur Bölümü */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Randevu Oluştur (İsteğe Bağlı)</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      IT Uzmanı Seçin
                    </label>
                    <select
                      value={formData.assigned_admin_id.toString()}
                      onChange={(e) => {
                        const adminId = parseInt(e.target.value) || 0;
                        setFormData({...formData, assigned_admin_id: adminId});
                        if (adminId) {
                          loadAdminAvailability(adminId);
                        } else {
                          setShowCalendar(false);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      size={3}
                    >
                      <option value="0">IT Uzmanı Seçin</option>
                      {users
                        .filter(u => u.role === 'admin' || u.role === 'superadmin')
                        .map(admin => (
                          <option key={admin.id} value={admin.id}>
                            {admin.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  {showCalendar && adminAvailability && (
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-700">
                      <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-3">
                        {loadingCalendar ? 'Takvim yükleniyor...' : 'Takvim - Müsait Saatleri Seçin'}
                      </h5>
                      {loadingCalendar ? (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">Yükleniyor...</p>
                      ) : (
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {adminAvailability?.available_slots?.map((slot: any, idx: number) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  scheduled_date: slot.date,
                                  scheduled_time: slot.time
                                });
                              }}
                              className={`w-full px-3 py-2 rounded text-sm font-medium transition ${
                                formData.scheduled_date === slot.date && formData.scheduled_time === slot.time
                                  ? 'bg-green-600 text-white'
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                              }`}
                            >
                              📅 {slot.date} ⏰ {slot.time}
                            </button>
                          ))}
                          {!adminAvailability?.available_slots || adminAvailability.available_slots.length === 0 && (
                            <p className="text-red-600 text-sm">Bu IT Uzmanında müsait slot bulunamadı</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setFormData({
                        user_id: 0,
                        old_phone_id: 0,
                        new_phone_id: 0,
                        replacement_reason: '',
                        notes: '',
                        scheduled_date: '',
                        scheduled_time: '',
                        assigned_admin_id: 0,
                        location_id: 0,
                        ticket_number: ''
                      });
                      setUserSearchTerm('');
                      setPhoneSearchTerm('');
                      setOldPhoneSearchTerm('');
                      setShowCalendar(false);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                  >
                    Kaydet
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Modal>

      {/* Appointment History Modal */}
      <Modal isOpen={showAppointmentHistory} onClose={() => setShowAppointmentHistory(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Randevu Geçmişim</h3>
                <button
                  onClick={() => setShowAppointmentHistory(false)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"
                >
                  ✕
                </button>
              </div>

              {userAppointments.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">Henüz randevu talebiniz bulunmamaktadır.</p>
              ) : (
                <div className="space-y-4">
                  {userAppointments.map((apt: any) => (
                    <div key={apt.id} className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 dark:text-gray-400 font-medium">Ticket Numarası</p>
                          <p className="text-gray-900 dark:text-gray-100">{apt.ticket_number}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400 font-medium">Durum</p>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            apt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            apt.status === 'confirmed' || apt.status === 'scheduled' ? 'bg-green-100 text-green-800' :
                            apt.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                          }`}>
                            {apt.status === 'pending' ? 'Beklemede' :
                             apt.status === 'confirmed' ? 'Onaylandı' :
                             apt.status === 'scheduled' ? 'Randevulu' :
                             apt.status === 'rejected' ? 'Reddedildi' :
                             apt.status}
                          </span>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400 font-medium">Tarih & Saat</p>
                          <p className="text-gray-900 dark:text-gray-100">
                            {new Date(apt.appointment_date).toLocaleDateString('tr-TR')} {apt.appointment_time}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400 font-medium">IT Uzmanı</p>
                          <p className="text-gray-900 dark:text-gray-100">{apt.admin_name || 'Atanmamış'}</p>
                        </div>
                      </div>
                      {apt.rejection_reason && (
                        <div className="mt-3 p-3 bg-red-50 border-l-4 border-red-500 text-sm text-red-700">
                          <strong>Red Sebebi:</strong> {apt.rejection_reason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default PhoneReplacementPage;